import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Avatar,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Badge,
  Circle,
  Icon,
  useColorModeValue,
  useToast,
  Tooltip,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import {
  FaChevronDown,
  FaTimes,
  FaRobot,
  FaCopy,
  FaStop,
  FaExpandAlt,
  FaCompressAlt,
} from 'react-icons/fa'
import { AnimatePresence } from 'framer-motion'
import { useNavigation } from '@/hooks/useNavigation'
import apiService from '@/services/api'
import { toConversationHistory, type ChatMessage } from './types'
import ReactMarkdown, { type Components } from 'react-markdown'
import { isUsageLimitError, useChatStream } from '@/hooks/useChatStream'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { MotionBox } from '@/components/Common/MotionBox'
import SourcePill from '@/components/Common/SourcePill'

// Utility component to render text with line breaks
const TextWithLineBreaks: React.FC<{ children: string; fontSize?: string }> = ({
  children,
  fontSize = 'sm',
}) => {
  const lines = children.split('\n')

  return (
    <>
      {lines.map((line, index) => (
        <Text key={index} fontSize={fontSize}>
          {line}
          {index < lines.length - 1 && <br />}
        </Text>
      ))}
    </>
  )
}

// Hoisted so a streaming answer doesn't rebuild the renderer map on every token
const markdownComponents: Components = {
  // Style paragraphs
  p: ({ children }) => (
    <Text fontSize="sm" mb={2} _last={{ mb: 0 }}>
      {children}
    </Text>
  ),
  // Style headings
  h1: ({ children }) => (
    <Text fontSize="lg" fontWeight="bold" mb={2}>
      {children}
    </Text>
  ),
  h2: ({ children }) => (
    <Text fontSize="md" fontWeight="bold" mb={2}>
      {children}
    </Text>
  ),
  h3: ({ children }) => (
    <Text fontSize="sm" fontWeight="bold" mb={1}>
      {children}
    </Text>
  ),
  // Style code blocks
  code: ({ children, className }) => {
    const isInline = !className
    if (isInline) {
      return (
        <Text
          as="code"
          bg="gray.100"
          px={1}
          borderRadius="sm"
          fontSize="xs"
          fontFamily="mono"
        >
          {children}
        </Text>
      )
    }
    return (
      <Box
        as="pre"
        bg="gray.100"
        p={2}
        borderRadius="md"
        fontSize="xs"
        fontFamily="mono"
        overflowX="auto"
        my={2}
      >
        <Text as="code">{children}</Text>
      </Box>
    )
  },
  // Style lists
  ul: ({ children }) => (
    <Box as="ul" pl={4} my={2}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box as="ol" pl={4} my={2}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Text as="li" fontSize="sm" mb={1}>
      {children}
    </Text>
  ),
  // Style links
  a: ({ children, href }) => (
    <Text
      as="a"
      href={href}
      color="blue.500"
      textDecoration="underline"
      _hover={{ color: 'blue.600' }}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </Text>
  ),
  // Style blockquotes
  blockquote: ({ children }) => (
    <Box
      borderLeft="4px solid"
      borderColor="gray.300"
      pl={4}
      py={2}
      my={2}
      bg="gray.50"
      borderRadius="sm"
    >
      {children}
    </Box>
  ),
  // Style strong/bold text
  strong: ({ children }) => (
    <Text as="strong" fontWeight="bold">
      {children}
    </Text>
  ),
  // Style emphasis/italic text
  em: ({ children }) => (
    <Text as="em" fontStyle="italic">
      {children}
    </Text>
  ),
}

// Markdown component for bot messages; memoized so only the message that changed re-parses
const BotMarkdownMessage = React.memo(function BotMarkdownMessage({ children }: { children: string }) {
  return <ReactMarkdown components={markdownComponents}>{children}</ReactMarkdown>
})

// localStorage helpers for session ID persistence
const getStoredSessionId = (ctxId: string): string | null =>
  localStorage.getItem(`chat_session_${ctxId}`)

const storeSessionId = (ctxId: string, sessionId: string): void =>
  localStorage.setItem(`chat_session_${ctxId}`, sessionId)

const removeStoredSessionId = (ctxId: string): void =>
  localStorage.removeItem(`chat_session_${ctxId}`)

const generateSessionId = (): string =>
  `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

export default function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false)
  // Expanded = larger, centered window over a blurred backdrop
  const [isExpanded, setIsExpanded] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [, setError] = useState<string | null>(null)
  const [conversationHistory, setConversationHistory] = useState<
    Map<
      string,
      {
        messages: ChatMessage[]
        sessionId: string
      }
    >
  >(new Map())
  const [lastContextId, setLastContextId] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  // Follow new text only while the reader is at the bottom; scrolling up pauses it
  const isNearBottomRef = useRef(true)
  const toast = useToast()
  const { t } = useAppTranslation(['common'])
  const { send: sendToChat, stop: stopAnswer, abort: abortAnswer, isStreaming } = useChatStream()

  const { selectedFolder, selectedFile } = useNavigation()

  // Colors
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const chatBgColor = useColorModeValue('gray.50', 'gray.900')

  // Memoize current context
  const currentContext = useMemo(() => {
    if (selectedFile) {
      return {
        type: 'file' as const,
        id: selectedFile.id,
        name: selectedFile.display_name || selectedFile.original_name || selectedFile.name,
      }
    }
    if (selectedFolder) {
      return {
        type: 'folder' as const,
        id: selectedFolder.id,
        name: selectedFolder.name,
      }
    }
    return null
  }, [selectedFile, selectedFolder])

  // Generate context-based identifier for conversation separation
  const currentContextId = useMemo(() => {
    if (!currentContext) return null
    return `${currentContext.type}_${currentContext.id}`
  }, [currentContext])

  // Get current session ID — localStorage first, then in-memory, then generate
  const currentSessionId = useMemo(() => {
    if (!currentContextId) return null
    const stored = getStoredSessionId(currentContextId)
    if (stored) return stored
    const existing = conversationHistory.get(currentContextId)?.sessionId
    return existing ?? generateSessionId()
  }, [currentContextId, conversationHistory])

  // Suggested questions based on context
  const suggestedQuestions = useMemo(() => {
    if (currentContext?.type === 'folder') {
      return t('chat.suggestedQuestions.folder', { returnObjects: true }) as string[]
    } else if (currentContext?.type === 'file') {
      return t('chat.suggestedQuestions.file', { returnObjects: true }) as string[]
    }
    return []
  }, [currentContext, t])

  // Auto-scroll to bottom when new messages arrive, unless the reader scrolled up.
  // While an answer streams, jump instead of animating so it doesn't stutter per token.
  useEffect(() => {
    if (messages.length > 0 && isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: isStreaming ? 'auto' : 'smooth', block: 'end' })
    }
  }, [messages, isStreaming])

  const handleMessagesScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }, [])

  // Save and restore conversation when switching between files/folders
  useEffect(() => {
    if (currentContextId && currentContextId !== lastContextId) {
      // An answer still streaming belongs to the old context; drop it so it can't land here
      abortAnswer()

      // Save current conversation if we're switching from another context
      if (lastContextId !== null && messages.length > 0) {
        setConversationHistory((prev) => {
          const newHistory = new Map(prev)
          const existingConversation = newHistory.get(lastContextId)
          newHistory.set(lastContextId, {
            messages: messages,
            sessionId: existingConversation?.sessionId || generateSessionId(),
          })
          return newHistory
        })
      }

      // Restore conversation for new context
      const existingConversation = conversationHistory.get(currentContextId)
      if (existingConversation) {
        setMessages(existingConversation.messages)
      } else {
        const storedSessionId = getStoredSessionId(currentContextId)
        if (storedSessionId) {
          // Load persisted history from database
          setIsLoadingHistory(true)
          apiService
            .getChatHistory(storedSessionId)
            .then(({ messages: rows }) => {
              const loaded: ChatMessage[] = rows.flatMap((row) => [
                {
                  id: `${row.created_at}_q`,
                  type: 'user' as const,
                  content: row.message,
                  timestamp: new Date(row.created_at),
                },
                {
                  id: `${row.created_at}_a`,
                  type: 'assistant' as const,
                  content: row.response,
                  timestamp: new Date(row.created_at),
                },
              ])
              setMessages(loaded)
              setConversationHistory((prev) => {
                const next = new Map(prev)
                next.set(currentContextId, { messages: loaded, sessionId: storedSessionId })
                return next
              })
            })
            .catch(() => {
              setMessages([])
            })
            .finally(() => setIsLoadingHistory(false))
        } else {
          setMessages([])
          const newSessionId = generateSessionId()
          storeSessionId(currentContextId, newSessionId)
          setConversationHistory((prev) => {
            const next = new Map(prev)
            next.set(currentContextId, { messages: [], sessionId: newSessionId })
            return next
          })
        }
      }

      setError(null)
      setLastContextId(currentContextId)
    }
  }, [currentContextId, lastContextId, messages, conversationHistory, abortAnswer])

  // Esc leaves the expanded view
  useEffect(() => {
    if (!isExpanded) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsExpanded(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isExpanded])

  const closeChat = useCallback(() => {
    setIsOpen(false)
    setIsExpanded(false)
  }, [])

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus()
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  const handleSendMessage = useCallback(async (messageOverride?: string) => {
    const content = (messageOverride ?? inputValue).trim()
    if (!content || !currentContext) return

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date(),
      context: currentContext,
    }

    // Persist session ID to localStorage so history survives page reloads
    if (currentContextId && currentSessionId) {
      storeSessionId(currentContextId, currentSessionId)
    }

    setMessages((prev) => {
      const newMessages = [...prev, userMessage]
      // Update conversation history with user message
      if (currentContextId) {
        setConversationHistory((historyPrev) => {
          const newHistory = new Map(historyPrev)
          const existingConversation = newHistory.get(currentContextId)
          newHistory.set(currentContextId, {
            messages: newMessages,
            sessionId: existingConversation?.sessionId || currentSessionId!,
          })
          return newHistory
        })
      }
      return newMessages
    })
    setInputValue('')
    setIsLoading(true)
    setIsTyping(true)
    setError(null)
    isNearBottomRef.current = true

    // One id for the whole answer so it grows in place instead of re-animating
    const assistantId = `${Date.now()}_a`
    const updateAnswer = (patch: Partial<ChatMessage>) =>
      setMessages((prev) =>
        prev.some((m) => m.id === assistantId)
          ? prev.map((m) => (m.id === assistantId ? { ...m, ...patch } : m))
          : [...prev, { id: assistantId, type: 'assistant', content: '', timestamp: new Date(), ...patch }]
      )

    // Mirror the finished conversation into the per-context cache
    const syncConversationCache = () =>
      setMessages((prev) => {
        if (currentContextId) {
          setConversationHistory((historyPrev) => {
            const newHistory = new Map(historyPrev)
            newHistory.set(currentContextId, { messages: prev, sessionId: currentSessionId! })
            return newHistory
          })
        }
        return prev
      })

    const result = await sendToChat(
      {
        question: content,
        session_id: currentSessionId!,
        ...(currentContext?.type === 'folder' ? { folder_id: currentContext.id } : {}),
        ...(currentContext?.type === 'file' ? { file_id: currentContext.id } : {}),
        conversation_history: toConversationHistory(messages),
      },
      {
        onText: (text) => {
          if (text === '') return
          setIsTyping(false)
          updateAnswer({ content: text })
        },
        onSources: (sources) => updateAnswer({ sources: sources.length ? sources : undefined }),
        onSuggestions: (questions) =>
          updateAnswer({ suggestedQuestions: questions.length ? questions : undefined }),
        onDone: (answer) => updateAnswer({ content: answer }),
      }
    )

    if (result.status === 'stopped' && result.text) {
      updateAnswer({ status: 'stopped' })
    } else if (result.status === 'error') {
      if (isUsageLimitError(result.error)) {
        // Quota exhausted — surface the limit as an assistant message instead of a toast
        updateAnswer({
          content: t('chatbot.limitExceeded', { ns: 'common' }),
          excludeFromHistory: true,
        })
      } else {
        if (result.text) {
          updateAnswer({ status: 'interrupted', excludeFromHistory: true })
        }
        setError(t('chat.errorGeneric', { ns: 'common' }))
        toast({
          title: t('messages.error', { ns: 'common' }),
          description: t('chat.errorGeneric', { ns: 'common' }),
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    }

    if (result.status !== 'aborted') {
      syncConversationCache()
    }

    setIsLoading(false)
    setIsTyping(false)
    // Add a small delay to ensure focus after DOM updates
    if (inputRef.current && isOpen) {
      setTimeout(() => {
        if (inputRef.current && isOpen) {
          inputRef.current.focus()
        }
      }, 100)
    }
  }, [inputValue, currentContext, isOpen, messages, currentSessionId, currentContextId, toast, t, sendToChat])

  const handleSuggestionClick = useCallback((suggestion: string) => {
    handleSendMessage(suggestion)
  }, [handleSendMessage])

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSendMessage()
      }
    },
    [handleSendMessage]
  )

  const clearChat = useCallback(async () => {
    if (currentSessionId) {
      await apiService.clearChatHistory(currentSessionId).catch(() => {})
    }

    setMessages([])
    setError(null)

    if (currentContextId) {
      removeStoredSessionId(currentContextId)
      const newSessionId = generateSessionId()
      storeSessionId(currentContextId, newSessionId)
      setConversationHistory((prev) => {
        const next = new Map(prev)
        next.set(currentContextId, { messages: [], sessionId: newSessionId })
        return next
      })
    }

    toast({
      title: t('chat.chatCleared', { ns: 'common' }),
      status: 'info',
      duration: 2000,
      isClosable: true,
    })
  }, [currentSessionId, currentContextId, toast, t])

  // Opened in a new tab: /preview/:fileId sits outside DashboardLayout, so navigating
  // in place would unmount this widget and close the conversation mid-thread.
  const openSource = useCallback((fileId: number) => {
    window.open(`${window.location.origin}/preview/${fileId}`, '_blank', 'noopener,noreferrer')
  }, [])

  const copyMessage = useCallback(
    (content: string) => {
      navigator.clipboard.writeText(content).then(() => {
        toast({
          title: 'Message copied',
          status: 'success',
          duration: 2000,
          isClosable: true,
        })
      })
    },
    [toast]
  )

  const formatTime = useCallback((timestamp: Date) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }, [])

  const hasWelcomeMessage = messages.length === 0 && currentContext

  // The chat window itself; rendered docked bottom-right or centered when expanded
  const chatWindow = (
    <Box
      width={isExpanded ? '100%' : { base: '90vw', md: '480px', xl: '520px' }}
      height={isExpanded ? '100%' : { base: '90vh', md: 'min(600px, calc(100vh - 120px))' }}
      maxHeight={isExpanded ? undefined : 'calc(100vh - 100px)'}
      minHeight={isExpanded ? undefined : { base: '400px', md: '450px' }}
      bg={bgColor}
      borderRadius="2xl"
      boxShadow="2xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {/* Enhanced Header */}
      <Box
        background="linear-gradient(135deg, var(--chakra-colors-brand-500), var(--chakra-colors-brand-700))"
        color="white"
        p={4}
        position="relative"
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={3}>
            <Avatar
              size="sm"
              bg="white"
              color="blue.600"
              icon={<FaRobot />}
              ring={2}
              ringColor="white"
            />
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="sm">
                {t('chat.aiAssistant', { ns: 'common' })}
              </Text>
              <HStack spacing={1} align="center">
                <Box w={2} h={2} bg="green.400" borderRadius="full" />
                <Text fontSize="xs" opacity={0.9}>
                  {t('chat.onlineInstantReplies', { ns: 'common' })}
                </Text>
              </HStack>
            </VStack>
          </HStack>

          <HStack spacing={2}>
            <Tooltip
              label={t(isExpanded ? 'chat.collapseChat' : 'chat.expandChat', { ns: 'common' })}
            >
              <IconButton
                icon={<Icon as={isExpanded ? FaCompressAlt : FaExpandAlt} />}
                variant="ghost"
                size="sm"
                color="white"
                _hover={{ bg: 'whiteAlpha.200' }}
                aria-label={t(isExpanded ? 'chat.collapseChat' : 'chat.expandChat', {
                  ns: 'common',
                })}
                onClick={() => setIsExpanded((v) => !v)}
              />
            </Tooltip>
            <Tooltip label={t('chat.minimizeChat', { ns: 'common' })}>
              <IconButton
                icon={<Icon as={FaChevronDown} />}
                variant="ghost"
                size="sm"
                color="white"
                _hover={{ bg: 'whiteAlpha.200' }}
                aria-label={t('chat.minimizeChat', { ns: 'common' })}
                onClick={closeChat}
              />
            </Tooltip>
            <Tooltip label={t('chat.closeChat', { ns: 'common' })}>
              <IconButton
                icon={<Icon as={FaTimes} />}
                variant="ghost"
                size="sm"
                color="white"
                _hover={{ bg: 'whiteAlpha.200' }}
                aria-label={t('chat.closeChat', { ns: 'common' })}
                onClick={closeChat}
              />
            </Tooltip>
          </HStack>
        </Flex>

        {/* Context Badge */}
        {currentContext && (
          <HStack mt={3} spacing={2}>
            <Badge
              bg="whiteAlpha.200"
              color="white"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="xs"
              fontWeight="medium"
            >
              <HStack spacing={1}>
                <Text>{currentContext.name}</Text>
              </HStack>
            </Badge>
            <Badge
              bg="whiteAlpha.200"
              color="white"
              px={2}
              py={1}
              borderRadius="full"
              fontSize="xs"
            >
              {currentContext.type.toUpperCase()}
            </Badge>
          </HStack>
        )}
      </Box>

      {/* Chat Messages Area */}
      <Box
        flex={1}
        minHeight="200px"
        overflowY="auto"
        p={4}
        bg={chatBgColor}
        onScroll={handleMessagesScroll}
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#CBD5E0',
            borderRadius: '2px',
          },
        }}
      >
        <VStack spacing={4} align="stretch">
          {/* Loading history indicator */}
          {isLoadingHistory && (
            <Flex justify="center" align="center" py={6}>
              <HStack spacing={2} color="gray.500">
                <Circle size="6px" bg="gray.400" animation="bounce 1.4s infinite" />
                <Circle size="6px" bg="gray.400" animation="bounce 1.4s infinite 0.2s" />
                <Circle size="6px" bg="gray.400" animation="bounce 1.4s infinite 0.4s" />
              </HStack>
            </Flex>
          )}

          {/* Welcome Message */}
          {hasWelcomeMessage && (
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Flex gap={3} align="start">
                <Avatar size="sm" bg="blue.500" color="white" icon={<FaRobot />} />
                <VStack align="start" spacing={2} flex={1}>
                  <Box
                    bg={bgColor}
                    p={3}
                    borderRadius="2xl"
                    borderTopLeftRadius="md"
                    boxShadow="sm"
                    border="1px solid"
                    borderColor={borderColor}
                    maxW="85%"
                  >
                    <Text fontSize="sm">
                      {t('chat.welcomeMessage', {
                        ns: 'common',
                        contextName: currentContext?.name,
                        contextType: currentContext?.type,
                      })}
                    </Text>
                  </Box>
                  <Text fontSize="xs" color="gray.500" ml={3}>
                    {t('chat.justNow', { ns: 'common' })}
                  </Text>
                </VStack>
              </Flex>
            </MotionBox>
          )}

          {/* Suggested Questions */}
          {hasWelcomeMessage && suggestedQuestions.length > 0 && (
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <VStack spacing={2} align="stretch">
                <Text fontSize="xs" color="gray.600" fontWeight="medium" textAlign="center">
                  {t('chat.tryAsking', { ns: 'common' })}
                </Text>
                {suggestedQuestions.map((suggestion, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    borderRadius="xl"
                    borderColor="blue.200"
                    color="blue.600"
                    _hover={{
                      bg: 'blue.50',
                      borderColor: 'blue.300',
                      transform: 'translateY(-1px)',
                    }}
                    transition="all 0.2s"
                    textAlign="left"
                    justifyContent="flex-start"
                    fontWeight="normal"
                    whiteSpace="normal"
                    height="auto"
                    py={2}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    ✨ {suggestion}
                  </Button>
                ))}
              </VStack>
            </MotionBox>
          )}

          {/* Dynamic Messages */}
          {messages.map((message) => (
            <MotionBox
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {message.type === 'user' ? (
                <Flex justify="flex-end" gap={3}>
                  <VStack align="end" spacing={1} flex={1}>
                    <Box
                      bg="blue.500"
                      color="white"
                      p={3}
                      borderRadius="2xl"
                      borderTopRightRadius="md"
                      maxW="85%"
                      alignSelf="flex-end"
                    >
                      <TextWithLineBreaks>{message.content}</TextWithLineBreaks>
                    </Box>
                    <Text fontSize="xs" color="gray.500" mr={3}>
                      {formatTime(message.timestamp)}
                    </Text>
                  </VStack>
                  <Avatar size="sm" bg="blue.100" color="blue.600" name="You" />
                </Flex>
              ) : (
                <Flex gap={3} align="start">
                  <Avatar size="sm" bg="blue.500" color="white" icon={<FaRobot />} />
                  <VStack align="start" spacing={1} flex={1} minW={0}>
                    <Box
                      bg={bgColor}
                      p={3}
                      borderRadius="2xl"
                      borderTopLeftRadius="md"
                      boxShadow="sm"
                      border="1px solid"
                      borderColor={borderColor}
                      maxW="85%"
                    >
                      <BotMarkdownMessage>{message.content}</BotMarkdownMessage>
                    </Box>
                    <HStack spacing={2}>
                      <Text fontSize="xs" color="gray.500">
                        {formatTime(message.timestamp)}
                      </Text>
                      <Tooltip label={t('chat.copyMessage', { ns: 'common' })}>
                        <IconButton
                          icon={<Icon as={FaCopy} />}
                          variant="ghost"
                          size="xs"
                          color="gray.400"
                          _hover={{ color: 'blue.500' }}
                          aria-label={t('chat.copyMessage', { ns: 'common' })}
                          onClick={() => copyMessage(message.content)}
                        />
                      </Tooltip>
                      {message.status && (
                        <Text fontSize="xs" color="gray.400" fontStyle="italic">
                          {t(message.status === 'stopped' ? 'chat.stopped' : 'chat.interrupted', {
                            ns: 'common',
                          })}
                        </Text>
                      )}
                    </HStack>
                    {message.sources && message.sources.length > 0 && (
                      <VStack spacing={1.5} align="stretch" w="full">
                        <Text fontSize="xs" color="gray.600" fontWeight="medium">
                          {t('chat.sources', { ns: 'common' })}
                        </Text>
                        <Wrap spacing={1.5}>
                          {message.sources.map((source) => (
                            <WrapItem key={source.file_id}>
                              <SourcePill
                                name={source.name}
                                mimeType={source.mime_type}
                                onClick={() => openSource(source.file_id)}
                              />
                            </WrapItem>
                          ))}
                        </Wrap>
                      </VStack>
                    )}
                    {message.suggestedQuestions && message.suggestedQuestions.length > 0 && (
                      <VStack spacing={2} align="stretch" w="full">
                        <Text fontSize="xs" color="gray.600" fontWeight="medium">
                          {t('chat.suggestedFollowUps', { ns: 'common' })}
                        </Text>
                        {message.suggestedQuestions.map((suggestion, index) => (
                          <Button
                            key={index}
                            variant="outline"
                            size="sm"
                            borderRadius="xl"
                            borderColor="blue.200"
                            color="blue.600"
                            _hover={{
                              bg: 'blue.50',
                              borderColor: 'blue.300',
                              transform: 'translateY(-1px)',
                            }}
                            transition="all 0.2s"
                            textAlign="left"
                            justifyContent="flex-start"
                            fontWeight="normal"
                            whiteSpace="normal"
                            height="auto"
                            py={2}
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            ✨ {suggestion}
                          </Button>
                        ))}
                      </VStack>
                    )}
                  </VStack>
                </Flex>
              )}
            </MotionBox>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Flex gap={3} align="start">
                <Avatar size="sm" bg="blue.500" color="white" icon={<FaRobot />} />
                <Box
                  bg={bgColor}
                  p={3}
                  borderRadius="2xl"
                  borderTopLeftRadius="md"
                  boxShadow="sm"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <HStack spacing={1}>
                    <Circle size="6px" bg="gray.400" animation="bounce 1.4s infinite" />
                    <Circle
                      size="6px"
                      bg="gray.400"
                      animation="bounce 1.4s infinite 0.2s"
                    />
                    <Circle
                      size="6px"
                      bg="gray.400"
                      animation="bounce 1.4s infinite 0.4s"
                    />
                  </HStack>
                </Box>
              </Flex>
            </MotionBox>
          )}

          <div ref={messagesEndRef} />
        </VStack>
      </Box>

      {/* Enhanced Input Area */}
      <Box p={4} bg={bgColor} borderTop="1px solid" borderColor={borderColor}>
        <VStack spacing={3}>
          {/* Quick Actions */}
          <HStack spacing={2} w="full" justify="flex-end">
            <Tooltip label={t('chat.clearChat', { ns: 'common' })}>
              <Button
                size="xs"
                variant="ghost"
                _hover={{ bg: 'gray.100' }}
                onClick={clearChat}
                isDisabled={messages.length === 0}
              >
                Clear
              </Button>
            </Tooltip>
          </HStack>

          {/* Input Field */}
          <InputGroup size="md">
            <Input
              ref={inputRef}
              placeholder={
                currentContext
                  ? t('chat.inputPlaceholder', {
                      ns: 'common',
                      contextType: currentContext.type,
                    })
                  : t('chat.selectContextFirst', { ns: 'common' })
              }
              bg="gray.50"
              border="1px solid"
              borderColor="gray.300"
              borderRadius="xl"
              _hover={{ borderColor: 'gray.400' }}
              _focus={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
              }}
              pr="3rem"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={!currentContext || isLoading}
            />
            {isStreaming && (
              <InputRightElement>
                <Tooltip label={t('chat.stop', { ns: 'common' })}>
                  <IconButton
                    icon={<Icon as={FaStop} boxSize={3} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    borderRadius="full"
                    aria-label={t('chat.stop', { ns: 'common' })}
                    onClick={stopAnswer}
                  />
                </Tooltip>
              </InputRightElement>
            )}
          </InputGroup>

          {/* Footer */}
          <Text fontSize="xs" color="gray.500" textAlign="center">
            {t('chat.poweredByAi', { ns: 'common' })}
          </Text>
        </VStack>
      </Box>
    </Box>
  )


  return (
    <Box
      position="fixed"
      bottom={{ base: '10px', md: '20px' }}
      right={{ base: '10px', md: '20px' }}
      top={{ base: '10px', md: 'auto' }}
      zIndex={1000}
      display="flex"
      flexDirection="column"
      alignItems="flex-end"
      justifyContent="flex-end"
    >
      <AnimatePresence>
        {isOpen && !isExpanded && (
          <MotionBox
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3 }}
            mb={4}
          >
            {chatWindow}
          </MotionBox>
        )}
      </AnimatePresence>

      {/* Expanded view: centered over a blurred backdrop; click outside or Esc to dock it again */}
      <AnimatePresence>
        {isOpen && isExpanded && (
          <MotionBox
            position="fixed"
            inset={0}
            zIndex={1400}
            bg="blackAlpha.500"
            backdropFilter="blur(6px)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={{ base: 2, md: 8 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e: React.MouseEvent) => {
              if (e.target === e.currentTarget) setIsExpanded(false)
            }}
          >
            <MotionBox
              width="min(1000px, 100%)"
              height="min(88vh, 900px)"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.2 }}
            >
              {chatWindow}
            </MotionBox>
          </MotionBox>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
        <IconButton
          icon={<Icon as={FaRobot} />}
          isRound
          size="lg"
          colorScheme="blue"
          aria-label={t('chat.toggleChatbot', { ns: 'common' })}
          onClick={() => setIsOpen(!isOpen)}
          bg="blue.500"
          color="white"
          _hover={{ bg: 'blue.600' }}
          boxShadow="lg"
        />
      </MotionBox>
    </Box>
  )
}
